import { TypeError } from '../../errors'
import type { Arr } from '../../interface'
import { assertArray } from '../../typeGuards/array'
import { assertStringOrRegularExpression, isAtom, isObj } from '../../typeGuards/dvala'
import { assertNumber } from '../../typeGuards/number'
import { assertString, assertStringOrNumber } from '../../typeGuards/string'
import { toFixedArity } from '../../utils/arity'
import { isPersistentVector, PersistentVector } from '../../utils/persistent'
import type { BuiltinNormalExpressions } from '../interface'

const blankRegexp = /^\s*$/
export const stringNormalExpression: BuiltinNormalExpressions = {
  'str': {
    evaluate: (params: Arr) => {
      let result = ''
      for (const param of params) {
        const paramStr
          = param === undefined || param === null
            ? ''
            : isAtom(param)
              ? `:${param.name}`
              : isObj(param)
                ? JSON.stringify(param.toRecord())
                : isPersistentVector(param)
                  ? JSON.stringify(param.toArray())
                  : `${param}`
        result = result + paramStr
      }
      return result
    },
    arity: {},
    docs: {
      type: '(() -> String) & ((Unknown, ...Unknown[]) -> String)',
      category: 'string',
      returns: { type: 'string' },
      args: { values: { type: 'any', rest: true } },
      variants: [{ argumentNames: ['values'] }],
      description: 'Concatenats `values` into one string. If `value` equals `null` empty string is returned. Atoms are converted to `":name"` format.',
      seeAlso: ['++', 'join', 'string.template', 'string.stringRepeat', 'number'],
      examples: [
        'str("A string", ", and another string", " ...and more")',
        'str("Just one string")',
        'str()',
        { code: 'str(0, false, true, null, #"^kalle", [1, 2, 3], {a: "a"})', noCheck: true },
        'str(:ok)',
      ],
      hideOperatorForm: true,
    },
  },

  'number': {
    evaluate: ([str], sourceCodeInfo): number => {
      assertString(str, sourceCodeInfo)
      const number = Number(str)
      if (Number.isNaN(number))
        throw new TypeError(`Could not convert '${str}' to a number.`, sourceCodeInfo)

      return number
    },
    arity: toFixedArity(1),
    docs: {
      type: '(String) -> Number',
      category: 'string',
      returns: { type: 'number' },
      args: { s: { type: 'string' } },
      variants: [{ argumentNames: ['s'] }],
      description: 'Parses `s` to a number.',
      seeAlso: ['str', 'isNumber', 'isInteger'],
      examples: [
        'number("10")',
        'number("010")',
        'number("-1.01")',
      ],
    },
  },

  'lowerCase': {
    evaluate: ([str], sourceCodeInfo): string => {
      assertString(str, sourceCodeInfo)
      return str.toLowerCase()
    },
    arity: toFixedArity(1),
    docs: {
      type: '(String) -> String',
      category: 'string',
      returns: { type: 'string' },
      args: { s: { type: 'string' } },
      variants: [{ argumentNames: ['s'] }],
      description: 'Returns `s` converted to lower case.',
      seeAlso: ['upperCase', 'string.capitalize'],
      examples: [
        'lowerCase("Albert")',
        'lowerCase("")',
      ],
    },
  },

  'upperCase': {
    evaluate: ([str], sourceCodeInfo): string => {
      assertString(str, sourceCodeInfo)
      return str.toUpperCase()
    },
    arity: toFixedArity(1),
    docs: {
      type: '(String) -> String',
      category: 'string',
      returns: { type: 'string' },
      args: { s: { type: 'string' } },
      variants: [{ argumentNames: ['s'] }],
      description: 'Returns `s` converted to upper case.',
      seeAlso: ['lowerCase', 'string.capitalize'],
      examples: [
        'upperCase("Albert")',
        'upperCase("")',
      ],
    },
  },

  'trim': {
    evaluate: ([str], sourceCodeInfo): string => {
      assertString(str, sourceCodeInfo)
      return str.trim()
    },
    arity: toFixedArity(1),
    docs: {
      type: '(String) -> String',
      category: 'string',
      returns: { type: 'string' },
      args: { s: { type: 'string' } },
      variants: [{ argumentNames: ['s'] }],
      description: 'Returns a new string with leading and trailing whitespaces removed.',
      seeAlso: ['string.trimLeft', 'string.trimRight', 'isBlank'],
      examples: [
        'trim("  Albert  ")',
        'trim("   ")',
        'trim("")',
      ],
    },
  },

  'join': {
    evaluate: ([stringList, delimiter], sourceCodeInfo): string => {
      assertArray(stringList, sourceCodeInfo)
      for (const str of stringList) assertStringOrNumber(str, sourceCodeInfo)
      assertString(delimiter, sourceCodeInfo)
      return [...stringList].join(delimiter)
    },
    arity: toFixedArity(2),
    docs: {
      type: '((String | Number)[], String) -> String',
      category: 'string',
      returns: { type: 'string' },
      args: {
        a: { type: 'array' },
        b: { type: 'string' },
        arr: { type: 'array' },
        delimiter: { type: 'string' },
      },
      variants: [{ argumentNames: ['arr', 'delimiter'] }],
      description: 'Returns a new string by concatenating all of the elements in `arr`, separated by `delimiter`.',
      seeAlso: ['split', 'str', '++', 'sequence.interpose'],
      examples: [
        'map([0, 1, 2, 3, 4, 5, 6, 7, 8, 9], str) join ", "',
        '([0, 1, 2, 3, 4, 5, 6, 7, 8, 9] map str) join ", "',
        'join(["Albert", 10], ", ")',
        'join(["Albert", "Mojir"], " ")',
        'join(map([0, 1, 2, 3, 4, 5, 6, 7, 8, 9], str), ", ")',
      ],
    },
  },

  'split': {
    evaluate: ([str, stringOrRegExpValue, limit], sourceCodeInfo): Arr => {
      assertString(str, sourceCodeInfo)
      assertStringOrRegularExpression(stringOrRegExpValue, sourceCodeInfo)
      if (limit !== undefined)
        assertNumber(limit, sourceCodeInfo, { integer: true, nonNegative: true })

      const delimiter
        = typeof stringOrRegExpValue === 'string'
          ? stringOrRegExpValue
          : new RegExp(stringOrRegExpValue.s, stringOrRegExpValue.f)
      return PersistentVector.from(str.split(delimiter, limit))
    },
    arity: { min: 2, max: 3 },
    docs: {
      type: '((String, String) -> String[]) & ((String, String, Number) -> String[]) & ((String, Regex) -> String[]) & ((String, Regex, Number) -> String[])',
      category: 'string',
      returns: { type: 'string', array: true },
      args: {
        a: { type: 'string' },
        b: { type: 'string' },
        s: { type: 'string' },
        delimiter: { type: 'string' },
        limit: { type: 'integer' },
      },
      variants: [
        { argumentNames: ['s', 'delimiter'] },
        { argumentNames: ['s', 'delimiter', 'limit'] },
      ],
      description: 'Divides `s` into an array of substrings. The division is done by searching for `delimiter`. If `limit` as provided, at most `limit` number of substrings are returned.',
      seeAlso: ['join', 'string.splitLines'],
      examples: [
        '"Albert Mojir" split " "',
        'split("Albert Mojir", " ")',
        'split("abcdefghijklmnopqrstuvw", #"[aoueiy]")',
        'split("0123456789", "")',
        'split("0123456789", "", 5) map number',
      ],
    },
  },
  'isBlank': {
    evaluate: ([value], sourceCodeInfo): boolean => {
      if (value === null) {
        return true
      }
      assertString(value, sourceCodeInfo)
      return blankRegexp.test(value)
    },
    arity: toFixedArity(1),
    docs: {
      type: '(String | Null) -> Boolean',
      category: 'string',
      returns: { type: 'boolean' },
      args: { s: { type: ['string', 'null'] } },
      variants: [{ argumentNames: ['s'] }],
      description: 'Returns true if `s` is null or only contains whitespace characters.',
      seeAlso: ['trim', 'isEmpty', 'isString'],
      examples: [
        'isBlank("")',
        'isBlank(null)',
        'isBlank("\n")',
        'isBlank(" ")',
        'isBlank(".")',
      ],
    },
  },
}
